/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CreditCardOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { shouldShowSportsPaySignupButton } from '@sportninja/common/features/account/sportsPaySignUp'
import type {
  SportsPayEligibleOrganization,
  SportsPayViewerPermission,
} from '@sportninja/common/features/account/sportsPaySignUp'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React, { useCallback, useState } from 'react'
import { paragraphSmall, weightSemiBold } from 'src/components/css'
import SportsPaySignupModal from './SportsPaySignupModal'

type SportsPaySignupButtonProps = {
  /** Organization the application is filed for. */
  organizationId?: string
  /**
   * The organization payload. Only the eligibility gate reads it —
   * `is_affiliate` plus `sportspay.is_onboarded` decide whether the signup
   * button is offered at all.
   */
  organization?: SportsPayEligibleOrganization
  /** The viewer's permissions on this organization; admin is required. */
  permission?: SportsPayViewerPermission
  /** Current organization's name, used to pre-fill the application. */
  organizationName?: string
}

/**
 * Primary-filled entry point to the SportsPay signup flow. Owns the modal's
 * open state so call sites only need to drop the button in.
 *
 * Renders nothing unless the organization is eligible — an affiliate that is
 * not yet onboarded (`shouldShowSportsPaySignupButton`) — or when there is no
 * organization id, since both onboarding endpoints are org-scoped and the modal
 * could neither read nor file an application without one.
 *
 * The eligibility gate covers THIS BUTTON only. The modal still runs its own
 * onboarding fetch, so it continues to catch an application filed elsewhere
 * since the organization payload was loaded.
 */
const SportsPaySignupButton: React.FC<SportsPaySignupButtonProps> = ({
  organizationId,
  organization,
  permission,
  organizationName,
}) => {
  'use memo'

  const [isOpen, setIsOpen] = useState(false)

  const handleOpen = useCallback(() => setIsOpen(true), [])
  const handleClose = useCallback(() => setIsOpen(false), [])

  if (
    !organizationId ||
    !shouldShowSportsPaySignupButton(organization, permission)
  ) {
    return null
  }

  return (
    <>
      <button type='button' onClick={handleOpen} css={buttonStyle}>
        <CreditCardOutlined css={iconStyle} />
        {capitalizeWords('Sign Up To Accept Credit Card Registrations')}
      </button>

      <SportsPaySignupModal
        isOpen={isOpen}
        onClose={handleClose}
        organizationId={organizationId}
        organizationName={organizationName}
      />
    </>
  )
}

export default SportsPaySignupButton

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const iconStyle = css`
  font-size: 16px;
  color: ${PRIMARY_BUTTON_CONTENT.icon};
`

const buttonStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  letter-spacing: 0.6px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  white-space: nowrap;
  cursor: pointer;
  background: ${colors.PRIMARY};
  color: ${PRIMARY_BUTTON_CONTENT.text};
  border: 1px solid ${colors.PRIMARY};
  padding: 8px;
  border-radius: 8px;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`
